import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { observer } from "mobx-react-lite";

import { DEFAULT_CURSOR_CONTEXT_ID } from "@/models/constants";
import { type DocumentModel } from "@/models/DocumentModel";

import type { CursorContext as CursorContextItem } from "@/models/CursorMapModel";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import type { Dispatch, PropsWithChildren, SetStateAction } from "react";

export type ContextType = {
  id: string;
  isFocused: boolean;
  setIsFocused: Dispatch<SetStateAction<boolean>>;
};

const CursorContext = createContext<ContextType>({
  id: DEFAULT_CURSOR_CONTEXT_ID,
  isFocused: false,
  setIsFocused: () => {},
});

type Props = {
  id: string;
  doc: DocumentModel;
  boundary?: InlineNodeModel | InlineNodeModel[];
};

/**
 * You can use this context provider to make the cursor work
 * in the same math expression that is rendered multiple times in the same document.
 * The cursor context will change automatically when clicking on the expression node.
 *
 * Example usage:
 *
 * ```
 * <CursorContextProvider id="unique-id" doc={doc}>
 *   <ExpressionNode node={inlineNode} />
 * </CursorContextProvider>
 * ```
 * ------------------------------------------------------------
 *
 * You also can change the current cursor context by setting the `doc.cursorContextId` property
 * in the components rendered inside the provider.
 * You can grab the context id with the `useCursorContextId` hook.
 *
 * Example usage:
 * ```
 * <CursorContextProvider id="unique-id" doc={doc}>
 *   <YourComponent node={inlineNode} />
 * </CursorContextProvider>
 * ```
 *
 * YourComponent.tsx
 *
 * ```
 * const YourComponent = observer(function YourComponent({ node }: { node: InlineNode }) {
 *   const cursorContextId = useCursorContextId();
 *
 *   const handleOnClick = () => {
 *     doc.transact(() => {
 *       doc.cursorContextId = cursorContextId;
 *     }, "setCursorContextId", { runInAction: true });
 *   }
 *   return (
 *     <>
 *       <button onClick={handleOnClick}>Change Cursor Context</button>
 *       <ExpressionNode node={node} />
 *     </>
 *   )
 * });
 * ```
 */
export const CursorContextProvider = observer(function CursorContextProvider({
  id,
  doc,
  boundary,
  children,
}: PropsWithChildren<Props>) {
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    doc.cursorMap.add(id);

    return () => {
      doc.cursorMap.remove(id);

      if (doc.cursorContextId === id) {
        doc.transact(
          () => {
            doc.cursorContextId = undefined;
          },
          "cursorContextCleanup",
          { runInAction: true },
        );
      }
    };
  }, [id, doc]);

  useEffect(() => {
    doc.cursorMap.setBoundary(id, boundary);
  }, [id, doc, boundary]);

  const providerValue = useMemo(() => {
    return {
      id,
      isFocused,
      setIsFocused,
    };
  }, [id, isFocused]);

  return <CursorContext.Provider value={providerValue}>{children}</CursorContext.Provider>;
});

export function useCursorContext(): ContextType {
  return useContext(CursorContext);
}

export function useCursorContextId(): string {
  return useCursorContext().id;
}

export function useCurrentCursorContext(doc?: DocumentModel): Partial<CursorContextItem> {
  const cursorContextId = useCursorContextId();

  if (!doc) {
    return {};
  }

  return doc.cursorContext(cursorContextId) ?? {};
}
